<template>
    <component
        :is="tag"
        v-bind="$attrs"
        class="app-panel stage-panel"
        :class="[
            `app-panel--${variant}`,
            {
                'app-panel--collapsible': collapsible,
                'app-panel--collapsed stage-panel--collapsed': collapsible && !resolvedOpen,
                'app-panel--overflow-visible': overflowVisible,
            },
            panelClass,
        ]"
    >
        <header v-if="hasHeader" class="app-panel-header stage-panel-header" :class="headerClass">
            <button
                v-if="collapsible"
                type="button"
                class="app-panel-toggle flex min-w-0 flex-1 items-center justify-between text-left focus:outline-none"
                :aria-controls="bodyId"
                :aria-expanded="resolvedOpen ? 'true' : 'false'"
                @click="toggle"
            >
                <slot name="header" :open="resolvedOpen">
                    <component :is="titleTag" class="stage-panel-header-label">{{ title }}</component>
                </slot>
                <svg-vue
                    class="ml-3 h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-150"
                    :class="resolvedOpen ? 'transform rotate-180' : ''"
                    icon="font-awesome.regular.angle-down"
                ></svg-vue>
            </button>

            <div v-else class="flex min-w-0 flex-1 items-center justify-between gap-4">
                <slot name="header" :open="true">
                    <component :is="titleTag" class="stage-panel-header-label">{{ title }}</component>
                </slot>
                <div v-if="$slots['header-actions']" class="flex flex-shrink-0 items-center">
                    <slot name="header-actions"></slot>
                </div>
            </div>

            <div v-if="collapsible && $slots['header-actions']" class="app-panel-header-actions flex flex-shrink-0 items-center">
                <slot name="header-actions"></slot>
            </div>
        </header>

        <div
            v-show="!collapsible || resolvedOpen"
            :id="bodyId"
            class="app-panel-body stage-panel-body"
            :class="bodyClass"
        >
            <slot></slot>
        </div>

        <footer
            v-if="$slots.footer"
            v-show="!collapsible || resolvedOpen"
            class="app-panel-footer stage-panel-footer"
            :class="footerClass"
        >
            <slot name="footer"></slot>
        </footer>
    </component>
</template>

<script>
let panelInstanceCounter = 0;

export default {
    name: 'app-panel',
    inheritAttrs: false,
    props: {
        title: {type: String, default: ''},
        tag: {type: String, default: 'section'},
        titleTag: {type: String, default: 'h2'},
        variant: {
            type: String,
            default: 'default',
            validator: (value) => ['default', 'compact'].includes(value),
        },
        collapsible: {type: Boolean, default: false},
        overflowVisible: {type: Boolean, default: false},
        open: {type: Boolean, default: undefined},
        defaultOpen: {type: Boolean, default: true},
        panelClass: {type: [String, Array, Object], default: ''},
        headerClass: {type: [String, Array, Object], default: ''},
        bodyClass: {type: [String, Array, Object], default: ''},
        footerClass: {type: [String, Array, Object], default: ''},
    },
    emits: ['update:open', 'toggle'],
    data() {
        panelInstanceCounter += 1;

        return {
            internalOpen: this.defaultOpen,
            bodyId: `app-panel-body-${panelInstanceCounter}`,
        };
    },
    computed: {
        hasHeader() {
            return Boolean(this.title || this.$slots.header || this.collapsible || this.$slots['header-actions']);
        },
        isControlled() {
            return typeof this.open === 'boolean';
        },
        resolvedOpen() {
            if (!this.collapsible) return true;

            return this.isControlled ? this.open : this.internalOpen;
        },
    },
    methods: {
        toggle() {
            if (!this.collapsible) return;

            const nextOpen = !this.resolvedOpen;
            if (!this.isControlled) {
                this.internalOpen = nextOpen;
            }

            this.$emit('update:open', nextOpen);
            this.$emit('toggle', nextOpen);
        },
    },
};
</script>

<style lang="scss">
.app-panel {
    height: auto;
}

.app-panel--overflow-visible {
    overflow: visible;
}

/*
 * Only the outer panel establishes visual elevation. Nested panels retain
 * their border and radius, but must not create a second shadow layer.
 */
.app-panel .app-panel {
    box-shadow: none;
}

.app-panel--collapsed.stage-panel--collapsed {
    border-bottom-width: 1px;
    border-bottom-right-radius: 0.25rem;
    border-bottom-left-radius: 0.25rem;
}

.app-panel--collapsed > .app-panel-header {
    border-bottom-color: transparent;
}

.app-panel--collapsible > .app-panel-header {
    display: flex;
    align-items: stretch;
    padding: 0;
}

.app-panel-toggle {
    min-height: 3.25rem;
    padding: 1rem 1.5rem;
}

.app-panel-header-actions {
    padding-right: 1.5rem;
}

.app-panel-footer {
    padding-right: 1rem;
}

.app-panel--compact > .app-panel-header {
    min-height: 2.75rem;
    padding: 0.75rem 1rem;
}

.app-panel--compact.app-panel--collapsible > .app-panel-header {
    padding: 0;
}

.app-panel--compact .app-panel-toggle {
    min-height: 2.75rem;
    padding: 0.75rem 1rem;
}

.app-panel--compact > .app-panel-body {
    padding: 1rem;
}

.app-panel--compact > .app-panel-footer {
    padding: 0.75rem 0.75rem 0.75rem 1rem;
}
</style>
